import { SwitchVertical01, SwitchVertical02 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import MapboxMap from 'src/app/Orders/OrderView/MapBoxMap';
import mapBox from 'src/assets/img/mapBox.png';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';

interface AddressData {
    fullAddress?: string;
    latitude?: string | number | null;
    longitude?: string | number | null;
    country?: string;
}

interface LaneOverviewModelProps {
    isOpen: boolean;
    handleClose: () => void;
    fromFullAddress: AddressData;
    toFullAddress: AddressData;
    // Client decision — multi-stop quotes: every stop strictly between from/to, in position order,
    // shown as a dot marker along one joined route (mirrors the Map-It widget's master route).
    // Optional/additive so the existing single-lane callers (e.g. MapRouteBlock.tsx) are unaffected.
    waypoints?: AddressData[];
}

const LaneOverviewModel: React.FC<LaneOverviewModelProps> = ({ handleClose, fromFullAddress, toFullAddress, waypoints = [] }) => {
    const markerRef = useRef<mapboxgl.Marker | null>(null);
    const [isSwapped, setIsSwapped] = useState(false);
    const [startPoint, setStartPoint] = useState<any>(null);
    const [endPoint, setEndPoint] = useState<any>(null);
    const [distance, setDistance] = useState<any>(null);
    const [duration, setDuration] = useState<any>(null);

    const handleSwapLocations = () => {
        setIsSwapped((prev) => !prev);
    };

    const displayedFromAddress = useMemo(() => (isSwapped ? toFullAddress : fromFullAddress), [isSwapped, fromFullAddress, toFullAddress]);

    const displayedToAddress = useMemo(() => (isSwapped ? fromFullAddress : toFullAddress), [isSwapped, fromFullAddress, toFullAddress]);

    // Swapping the endpoints must also reverse waypoint order — the route is still driven
    // sequentially start -> waypoints -> end, just in the opposite physical direction.
    const displayedWaypoints = useMemo(() => (isSwapped ? [...waypoints].reverse() : waypoints), [isSwapped, waypoints]);

    const midpoints = useMemo(
        () =>
            displayedWaypoints
                .filter((wp) => wp?.latitude !== null && wp?.latitude !== undefined && wp?.longitude !== null && wp?.longitude !== undefined)
                .map((wp) => ({
                    lat: typeof wp.latitude === 'string' ? parseFloat(wp.latitude) : (wp.latitude as number),
                    lng: typeof wp.longitude === 'string' ? parseFloat(wp.longitude as string) : (wp.longitude as number),
                    name: null,
                })),
        [displayedWaypoints]
    );

    useEffect(() => {
        if (displayedFromAddress?.latitude) {
            setStartPoint({
                lat: typeof displayedFromAddress.latitude === 'string' ? parseFloat(displayedFromAddress.latitude) : displayedFromAddress.latitude,
                lng: typeof displayedFromAddress.longitude === 'string' ? parseFloat(displayedFromAddress.longitude as string) : displayedFromAddress.longitude,
                name: null,
            });
        } else {
            setStartPoint(null);
            setDistance(null);
            setDuration(null);
        }
    }, [displayedFromAddress]);

    useEffect(() => {
        if (displayedToAddress?.latitude) {
            setEndPoint({
                lat: typeof displayedToAddress.latitude === 'string' ? parseFloat(displayedToAddress.latitude) : displayedToAddress.latitude,
                lng: typeof displayedToAddress.longitude === 'string' ? parseFloat(displayedToAddress.longitude as string) : displayedToAddress.longitude,
                name: null,
            });
        } else {
            setEndPoint(null);
            setDistance(null);
            setDuration(null);
        }
    }, [displayedToAddress]);

    useEffect(() => {
        if (startPoint && endPoint) {
            setDistance(null);
            setDuration(null);
        }
    }, [startPoint?.lat, startPoint?.lng, endPoint?.lat, endPoint?.lng]);

    return (
        <CommonModal title="Lane Overview" handleClose={handleClose} modalFooterClass="!border-0 !bg-transparent" modalClassName="flex flex-col p-0" size="max-w-4xl">
            <div className="p-4">
                <div className="rounded-xl border border-utilityGray200 bg-white p-4 space-y-4">
                    <div className="rounded-lg border border-utilityGray200 bg-gray50 shadow-[inset_0_2px_4px_-2px_rgba(16,24,40,0.06)] p-3">
                        <ul className="space-y-0.5">
                            <li className="relative flex items-start gap-x-3 overflow-hidden pr-[34px]">
                                <div className="after:content-[''] after:h-full after:w-[2px] after:rounded-sm after:top-0 after:absolute flex items-center justify-center after:border-gray400 after:border-dashed after:border-l">
                                    <div className="w-[20px] h-[20px] bg-gray50 flex justify-center items-center z-[2]">
                                        <StatusDotProfileCmp dotColor="green" />
                                    </div>
                                </div>

                                <div className="flex-1 text-grayLight900 text-xs font-medium overflow-hidden">
                                    <div className="text-textSecondary text-sm font-normal truncate cursor-default">{displayedFromAddress?.fullAddress}</div>
                                </div>
                            </li>
                            <li className="relative flex items-start gap-x-3 overflow-hidden !-my-[7px]">
                                <div className="after:content-[''] after:h-full after:w-[2px] after:rounded-sm after:top-0 after:absolute flex items-center justify-center after:border-gray400 after:border-dashed after:border-l">
                                    <div className="w-[20px] h-[20px] flex justify-center items-center"></div>
                                </div>

                                <div className="flex-1 flex items-center gap-x-4">
                                    <p className="flex-1 border-t border-utilityGray200"></p>
                                    <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={handleSwapLocations}>
                                        {isSwapped ? <SwitchVertical01 className="w-3.5 h-3.5 text-grayLight900" /> : <SwitchVertical02 className="w-3.5 h-3.5 text-grayLight900" />}
                                    </ButtonCmp>
                                </div>
                            </li>
                            <li className="relative flex gap-x-3 overflow-hidden pr-[34px]">
                                <div className="after:content-[''] after:h-0 after:w-[2px] after:rounded-sm after:top-0 after:absolute flex items-center justify-center after:bg-successSecondary">
                                    <div className="w-[20px] h-[20px] bg-gray50 flex justify-center items-center z-[2]">
                                        <StatusDotProfileCmp dotColor="primary" />
                                    </div>
                                </div>

                                <div className="flex-1 text-grayLight900 text-xs font-medium overflow-hidden">
                                    <div className="text-textSecondary text-sm font-normal truncate cursor-default">{displayedToAddress?.fullAddress}</div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Map Container */}
                    <div className="relative rounded-lg overflow-hidden border border-utilityGray200 w-full h-[500px]">
                        <MapboxMap
                            startPoint={startPoint}
                            endPoint={endPoint}
                            midpoints={midpoints.length > 0 ? midpoints : undefined}
                            height="100%"
                            className="map-container"
                            markerRef={markerRef}
                            isShowRoute={!!(startPoint && endPoint)}
                            isZoomable={true}
                            isDraggable={true}
                            isFullscreen={false}
                            defaultZoom={2}
                            setDistance={setDistance}
                            setDuration={setDuration}
                            preventCrossBorder={displayedFromAddress?.country && displayedToAddress?.country && displayedFromAddress?.country === displayedToAddress?.country}
                            showMapControls={true}
                            startMarkerColor="#17B26A"
                            endMarkerColor="#444CE7"
                        />
                        {distance && duration && (
                            <div className="absolute bottom-4 right-4 px-1.5 py-1 bg-white rounded flex items-center gap-1.5 shadow-lg">
                                <img src={mapBox} className="w-4 h-4" />
                                <p className="text-grayLight900 text-sm font-medium">
                                    {distance} miles
                                    <span className="text-borderPrimary mx-1">|</span>
                                    {duration}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default LaneOverviewModel;
